import Alert from '@liquidweb-dev/undercurrent/Alert';
import AlertTitle from '@liquidweb-dev/undercurrent/AlertTitle';
import { Link as RouterLink } from 'react-router-dom';
import Link from '@liquidweb-dev/undercurrent/Link';
import useAccountDetails from 'modules/queries/account/useDetails';
import useAssetDetails from 'modules/queries/asset/useDetails';
import useUserMetaStateProgressiveDismissal from 'modules/upsell/useUserMetaStateProgressiveDismissal';
import sendGtmEvent from 'utility/gtm/sendGtmEvent';
import hostingDetailsAlsowith from 'pages/servers/details/hostingDetailsAlsowith';

interface BackupsAlertProps {
	uniqId: string;
}

const BackupsAlert = ({ uniqId }: BackupsAlertProps) => {
	const { data: accountData } = useAccountDetails({});
	const { data: assetDetailsData } = useAssetDetails({
		uniq_id: uniqId,
		alsowith: hostingDetailsAlsowith,
	});

	const backupsState = assetDetailsData?.backupsState;
	const acronisStatus = assetDetailsData?.acronisStatus;
	const displayType = assetDetailsData?.displayType;

	const canBackups = Boolean(
		backupsState?.canAcronis || backupsState?.canCloudBackup,
	);
	const hasBackups = backupsState?.hasBackups ?? false;
	const isSettingUp = hasBackups && acronisStatus && acronisStatus !== 'Active';

	const { isDismissed, dismiss } = useUserMetaStateProgressiveDismissal(
		`backup_alert_dismissed_${uniqId}`,
	);

	const handleDismiss = () => {
		dismiss();
		sendGtmEvent({
			event: 'lw.event',
			eventName: 'backup_alert_dismissed',
			eventData: {
				account_id: accountData?.accnt,
				server_id: uniqId,
				product_type: displayType,
				timestamp: new Date().toISOString(),
			},
		});
	};

	// Show alert only when:
	// - Product supports backups (canBackups)
	// - Backups are not enabled (!hasBackups)
	// - Backups are not setting up (!isSettingUp)
	// - Alert has not been dismissed (!isDismissed)
	if (!canBackups || hasBackups || isSettingUp || isDismissed) {
		return null;
	}

	const backupsUrl = `/servers/${uniqId}/backups`;

	return (
		<Alert severity="warning" dismissable onDismiss={handleDismiss}>
			<AlertTitle>Your backups are not configured.</AlertTitle>
			Without backups, data loss from outages or mistakes may be permanent.
			Manage your{' '}
			<Link component={RouterLink} to={backupsUrl}>
				Backups
			</Link>{' '}
			below to set them up now.
		</Alert>
	);
};

export default BackupsAlert;
